import { BubbleMenu } from '@tiptap/react/menus'
import { useState, useCallback } from 'react'
import type { Editor } from '@tiptap/react'

interface EditorBubbleMenuProps {
  editor: Editor
}

export default function EditorBubbleMenu({ editor }: EditorBubbleMenuProps) {
  const [linkInputActive, setLinkInputActive] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')

  // Prevent editor blur when clicking bubble menu buttons.
  // Without this, ActiveBlockFlattener's blur handler unflatten the block
  // before onClick fires, invalidating the selection.
  const preventEditorBlur = (e: React.MouseEvent) => {
    if (!(e.target instanceof HTMLInputElement)) {
      e.preventDefault()
    }
  }

  const shouldShow = useCallback(({ editor, state }: { editor: Editor; state: { selection: { empty: boolean } } }) => {
    if (state.selection.empty) return false
    if (editor.isActive('codeBlock')) return false
    if (editor.isActive('image')) return false
    return true
  }, [])

  const openLinkInput = () => {
    const existingHref = editor.getAttributes('link').href || ''
    setLinkUrl(existingHref)
    setLinkInputActive(true)
  }

  const closeLinkInput = () => {
    setLinkInputActive(false)
    setLinkUrl('')
  }

  const submitLink = () => {
    if (linkUrl.trim()) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: linkUrl.trim() }).run()
    } else {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
    }
    closeLinkInput()
  }

  const removeLink = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run()
    closeLinkInput()
  }

  const handleLinkKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      submitLink()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      closeLinkInput()
      editor.commands.focus()
    }
  }

  if (linkInputActive) {
    return (
      <BubbleMenu editor={editor} shouldShow={shouldShow}>
        <div className="bubble-menu" onMouseDown={preventEditorBlur}>
          <input
            className="bubble-menu-link-input"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={handleLinkKeyDown}
            placeholder="https://..."
            autoFocus
          />
          <button onClick={submitLink} title="Apply link">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
          </button>
          {editor.isActive('link') && (
            <button onClick={removeLink} title="Remove link">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>
          )}
          <button onClick={closeLinkInput} title="Cancel">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5" /><polyline points="12 19 5 12 12 5" /></svg>
          </button>
        </div>
      </BubbleMenu>
    )
  }

  return (
    <BubbleMenu editor={editor} shouldShow={shouldShow}>
      <div className="bubble-menu" onMouseDown={preventEditorBlur}>
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={editor.isActive('bold') ? 'is-active' : ''}
          title="Bold"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" /><path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" /></svg>
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={editor.isActive('italic') ? 'is-active' : ''}
          title="Italic"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="4" x2="10" y2="4" /><line x1="14" y1="20" x2="5" y2="20" /><line x1="15" y1="4" x2="9" y2="20" /></svg>
        </button>
        <button
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={editor.isActive('strike') ? 'is-active' : ''}
          title="Strikethrough"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4H9a3 3 0 0 0-3 3c0 1.4.8 2.6 2 3.2" /><path d="M22 12H2" /><path d="M8 12c0 2.8 2.2 5 5 5h3a3 3 0 0 0 0-6" /></svg>
        </button>
        <button
          onClick={() => editor.chain().focus().toggleCode().run()}
          className={editor.isActive('code') ? 'is-active' : ''}
          title="Inline code"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" /></svg>
        </button>

        <span className="bubble-menu-separator" />

        <button
          onClick={openLinkInput}
          className={editor.isActive('link') ? 'is-active' : ''}
          title="Link"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
        </button>

        <span className="bubble-menu-separator" />

        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive('bulletList') ? 'is-active' : ''}
          title="Bullet list"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><circle cx="3" cy="6" r="1" fill="currentColor" /><circle cx="3" cy="12" r="1" fill="currentColor" /><circle cx="3" cy="18" r="1" fill="currentColor" /></svg>
        </button>
        <button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editor.isActive('orderedList') ? 'is-active' : ''}
          title="Ordered list"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="10" y1="6" x2="21" y2="6" /><line x1="10" y1="12" x2="21" y2="12" /><line x1="10" y1="18" x2="21" y2="18" /><text x="1" y="8" fontSize="8" fill="currentColor" fontFamily="sans-serif" stroke="none">1</text><text x="1" y="14" fontSize="8" fill="currentColor" fontFamily="sans-serif" stroke="none">2</text><text x="1" y="20" fontSize="8" fill="currentColor" fontFamily="sans-serif" stroke="none">3</text></svg>
        </button>
        <button
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={editor.isActive('blockquote') ? 'is-active' : ''}
          title="Blockquote"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V21" /><path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3" /></svg>
        </button>
      </div>
    </BubbleMenu>
  )
}
